import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogContent,
    Typography,
    Button,
    Box,
    IconButton,
    Snackbar,
    Alert,
    Paper, Grid
} from '@mui/material';
import { X, Copy } from 'lucide-react';
import QRCode from 'react-qr-code';
import { api } from '../../services/api';
import { AlertInfo } from '../../components/Alerts/Alert';
import { useNavigate } from 'react-router-dom';

interface PixPaymentModalProps {
    open: boolean;
    onClose: () => void;
    pixCode: string;
    paymentId: string | null
    channel?: string
}

const PixPaymentModal: React.FC<PixPaymentModalProps> = ({ open, onClose, pixCode, paymentId, channel = '/license' }) => {
    const navigate = useNavigate();
    const [copiedSnackbar, setCopiedSnackbar] = useState(false);
    const [check, setCheck] = useState(0);

    const handleCopyPixCode = () => {
        navigator.clipboard.writeText(pixCode);
        setCopiedSnackbar(true);
    };

    const handleCloseSnackbar = () => {
        setCopiedSnackbar(false);
    };

    async function consultPayment() {
        setTimeout(async() => {
            try {
                const response = await api.get(`/consultpayment/${paymentId}`);
                console.log(response);
                if(response.data && response.data.id_pagamento){
                    if(response.data.status_pagamento === 'RECEIVED' || response.data.status_pagamento === 'CONFIRMED'){
                        onClose();
                        return AlertInfo({icon: 'success', title: 'Pagamento Confirmado'}).then(() => {
                            sessionStorage.removeItem('isDisabled');
                            navigate(channel);
                        });
                    }
                }
                setCheck(check + 1);
            } catch (error) {
                console.log(error);
            }
        }, 5000);
    }

    useEffect(() => {
        if(open) consultPayment();
    }, [open, check]);

    return (
        <>
            <Dialog
                open={open}
                maxWidth="sm"
                fullWidth
            >
                <Box sx={{ position: 'relative', p: 0 }}>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)'
                            }
                        }}
                    >
                        <X size={20} />
                    </IconButton>

                    <DialogContent sx={{ p: 4, pb: 3 }}>
                        <Grid container spacing={0}>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    sx={{
                                        fontWeight: 600,
                                        textAlign: 'left'
                                    }}
                                >
                                    Forma de pagamento
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    component="h3"
                                    sx={{
                                        fontWeight: 500,
                                        textAlign: 'left'
                                    }}
                                >
                                    Pix
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        mb: 3,
                                        border: '2px solid #e0e0e0',
                                        borderRadius: 2,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: 200,
                                        height: 200,
                                        backgroundColor: '#fafafa'
                                    }}
                                >
                                    <QRCode value={pixCode} />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: '#000',
                                        textAlign: 'justify',
                                        lineHeight: 1.5,
                                        maxWidth: 320
                                    }}
                                >
                                    Acesse seu APP de pagamentos e faça a leitura do QR Code ao lado para efetuar o pagamento de forma rápida e segura.
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    component="h4"
                                    sx={{
                                        fontWeight: 500,
                                        color: '#333',
                                        mb: 2,
                                        textAlign: 'left'
                                    }}
                                >
                                    Código Pix copia e cola
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        flex: 1,
                                        p: 2,
                                        backgroundColor: '#f5f5f5',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 1
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#333',
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            lineHeight: 1.4,
                                            wordBreak: 'break-all'
                                        }}
                                    >
                                        {pixCode}
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} sm={4} container alignItems="center">
                                <Button
                                    variant="contained"
                                    onClick={handleCopyPixCode}
                                    startIcon={<Copy size={16} />}
                                    color='primary'
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        px: 3,
                                        py: 1.5,
                                        ml: 1,
                                        minWidth: 100,
                                        borderRadius: 1,
                                    }}
                                >
                                    Copiar
                                </Button>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Box>
            </Dialog>

            <Snackbar
                open={copiedSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Código PIX copiado com sucesso!
                </Alert>
            </Snackbar>
        </>
    );
};

export default PixPaymentModal;